import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { trackFunnel } from "@/lib/analytics";
import { useOnboardingStore } from "@/stores/onboarding";

export const Route = createFileRoute("/onboarding/alerts")({
  component: AlertsStep,
});

function AlertsStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const channels = useOnboardingStore((s) => s.channels);
  const setChannels = useOnboardingStore((s) => s.setChannels);
  const alertLanguage = useOnboardingStore((s) => s.alertLanguage);
  const setAlertLanguage = useOnboardingStore((s) => s.setAlertLanguage);
  const phone = useOnboardingStore((s) => s.phone);

  const hasPhone = Boolean(phone && String(phone).trim());
  const anyChannel =
    channels.sms || channels.push || (hasPhone && channels.whatsapp) || channels.email;

  function toggleWhatsapp(v: boolean) {
    if (!hasPhone) return;
    setChannels({ whatsapp: v });
  }

  return (
    <div className="flex flex-1 flex-col">
      <h1 className="font-display text-3xl font-bold">{t("onboarding.alerts.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("onboarding.alerts.body")}</p>
      <p className="mt-3 rounded-md bg-muted px-3 py-2 text-sm text-foreground">
        {t("onboarding.alerts.allAlertsInfo")}
      </p>

      <div className="mt-6 space-y-4">
        {(
          [
            ["sms", t("onboarding.alerts.channelSms")],
            ["push", t("onboarding.alerts.channelPush")],
            ["whatsapp", t("onboarding.alerts.channelWhatsapp")],
            ["email", t("onboarding.alerts.channelEmail")],
          ] as const
        ).map(([key, label]) => {
          const whatsappLocked = key === "whatsapp" && !hasPhone;
          return (
            <div
              key={key}
              className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-3"
            >
              <div>
                <Label htmlFor={`channel-${key}`}>{label}</Label>
                {whatsappLocked ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("onboarding.alerts.whatsappNeedsPhone")}
                  </p>
                ) : null}
                {key === "whatsapp" && hasPhone ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("onboarding.alerts.whatsappOptIn")}
                  </p>
                ) : null}
              </div>
              <Switch
                id={`channel-${key}`}
                checked={key === "whatsapp" ? Boolean(hasPhone && channels.whatsapp) : channels[key]}
                disabled={whatsappLocked}
                onCheckedChange={(v) => {
                  if (key === "whatsapp") toggleWhatsapp(v);
                  else setChannels({ [key]: v });
                }}
              />
            </div>
          );
        })}

        <div>
          <Label className="text-xs font-semibold">{t("onboarding.alerts.alertLanguage")}</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={alertLanguage === "en" ? "default" : "outline"}
              onClick={() => setAlertLanguage("en")}
            >
              {t("common.english")}
            </Button>
            <Button
              type="button"
              variant={alertLanguage === "sw" ? "default" : "outline"}
              onClick={() => setAlertLanguage("sw")}
            >
              {t("common.swahili")}
            </Button>
          </div>
        </div>
      </div>

      {!anyChannel ? (
        <p role="alert" className="mt-4 text-xs text-severe">
          {t("onboarding.alerts.needChannel")}
        </p>
      ) : null}

      <div className="mt-auto space-y-2 pt-8">
        <Button
          className="w-full"
          disabled={!anyChannel}
          onClick={() => {
            trackFunnel("step_completed", "alerts");
            void navigate({ to: "/onboarding/consent" });
          }}
        >
          {t("common.continue")}
        </Button>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => void navigate({ to: "/onboarding/persona" })}
        >
          {t("common.back")}
        </Button>
      </div>
    </div>
  );
}
